import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Mail, FileText, Building2 } from "lucide-react";

interface IndustryModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'other' | 'coming-soon';
  selectedIndustry?: string;
}

export const IndustryModal = ({ isOpen, onClose, type, selectedIndustry }: IndustryModalProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [industryName, setIndustryName] = React.useState('');
  const [description, setDescription] = React.useState('');

  console.log('Rendering IndustryModal, type:', type);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting form:', { email, industryName, description });
    
    toast({
      title: "Thank you!",
      description: type === 'other' 
        ? "We'll review your industry submission."
        : "We'll notify you when this assessment becomes available.",
    });
    
    onClose();
  };

  const handleExploreArtifacts = () => {
    navigate('/artifacts');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-card-bg border-border">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl font-bold">
            {type === 'other' ? 'Tell us about your industry' : 'Coming Soon!'}
          </DialogTitle>
          <DialogDescription className="text-text-secondary text-base">
            {type === 'other' 
              ? 'Help us understand your industry better'
              : `${selectedIndustry} assessment is coming soon.`}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {type === 'other' && (
            <>
              <div className="space-y-2">
                <label htmlFor="industryName" className="text-sm font-medium text-text-primary">
                  Industry Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-5 w-5 text-text-muted" />
                  <Input
                    id="industryName"
                    value={industryName}
                    onChange={(e) => setIndustryName(e.target.value)}
                    className="pl-10 bg-card-bg border-border text-text-primary h-12"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium text-text-primary">
                  Optional Description
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-5 w-5 text-text-muted" />
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us more about your industry..."
                    className="pl-10 bg-card-bg border-border text-text-primary min-h-[100px] resize-none"
                  />
                </div>
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-text-primary">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-text-muted" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="pl-10 bg-card-bg border-border text-text-primary h-12"
              />
            </div>
            {type === 'coming-soon' && (
              <p className="text-sm text-text-secondary mt-1">
                Get notified when it's ready
              </p>
            )}
          </div>
          
          <div className="space-y-3 pt-2">
            <Button 
              type="submit" 
              className="w-full h-12 text-base font-medium bg-primary hover:bg-primary-hover"
            >
              Submit
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleExploreArtifacts}
              className="w-full h-12 text-base font-medium border-border bg-transparent hover:bg-card-hover"
            >
              Explore Career Artifacts Instead
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};